/**
 * STEP MOA-LIVE-ASSISTANT-UI-PREP-1 · 라모 AI 음성 도우미 캡슐
 * STEP MOA-LIVE-ASSISTANT-MINI-ICON-1 · controlsVisible OFF 시 36px 미니 아이콘
 * STEP MOA-LIVE-VOICE-1B · voice prop 수신 + PTT 버튼 · AI ON/OFF 활성화
 * ============================================================================
 * Props:
 *   voice (optional) · useRamoVoice 반환 객체.  미제공 시 "준비 중" (UI-only) 모드.
 *
 * 상태 라벨 (voice.status 가 있으면 그 값 우선, 없으면 UI-only off).
 *
 * UI 접힘(expanded)과 AI 기능(voice.enabled)은 **별개** 개념 — 접기와 OFF 를 혼동 금지.
 */
import React, { useEffect, useState, useRef, useCallback } from 'react';
import { Sparkles, X, Mic, History, Settings as SettingsIcon } from 'lucide-react';
import { RamoAutoVoicePanel, AUTO_LABELS } from '@/components/seller/live/RamoAutoVoicePanel';

const STATUS_LABELS = {
  off: '라모 AI 꺼짐',
  idle: '라모 AI',
  detected: '라모야?',
  awaiting: '명령을 말씀하세요',
  listening: '듣고 있어요…',
  processing: '처리 중…',
  done: '완료',
  failed: '명령 실패',
};

const DIAMOND = '\u25C6';

export const RamoAiCapsule = ({
  className = '',
  style = null,
  variant = 'mobile',
  forceCollapsed = false,
  testIdPrefix = 'live-console-ramo',
  voice = null,
  // STEP MOA-LIVE-VOICE-1C · "라모야" 자동 호출어 토글 · state 노출.
  //   { on, state: 'idle'|'loading'|'ready'|'error'|'ios_hold', error, toggle, isIOS }
  wakeWord = null,
  autoVoice = null,
}) => {
  const [expanded, setExpanded] = useState(false);

  // voice prop 가 없으면 UI-only 준비 중 모드 (기존 STEP UI-PREP 동작).
  const wired = !!voice;
  const enabled = wired ? !!voice.enabled : false;
  const status = wired ? (voice.status || (enabled ? 'idle' : 'off')) : 'off';
  const history = wired ? (Array.isArray(voice.history) ? voice.history : []) : [];
  const lastResult = wired ? (voice.lastResult || '') : '';

  // STEP MINI_ICON · 외부에서 (예: controlsVisible OFF) 패널만 접도록 요청.
  // 사용자가 아이콘을 직접 눌러 패널을 다시 여는 것은 허용 (영구 강제 닫힘 금지).
  useEffect(() => {
    if (forceCollapsed) setExpanded(false);
  }, [forceCollapsed]);

  const wakeReady = !!wakeWord?.on && wakeWord?.state === 'ready';
  const autoActive = !!autoVoice && (autoVoice.on || autoVoice.phase === 'limited' || autoVoice.phase === 'paused')
    && !['listening', 'processing', 'done', 'failed', 'awaiting'].includes(status);
  const capsuleLabel =
    autoActive ? (AUTO_LABELS[autoVoice.phase] || '음성 대기')
    : status === 'awaiting' ? '명령을 말씀하세요'
    : status === 'listening' ? '듣고 있어요…'
    : status === 'processing' ? '처리 중…'
    : status === 'detected' ? '라모야?'
    : status === 'done' ? '완료'
    : status === 'failed' ? '명령 실패'
    : enabled && wakeReady ? '"라모야" 호출 대기'
    : enabled ? '라모 AI'
    : '라모 AI · 준비 중';

  const isMiniIcon = !!forceCollapsed;

  // ─ PTT · press-and-hold on dedicated button inside panel ──────────────
  const holdingRef = useRef(false);
  const onPttDown = useCallback((e) => {
    if (!wired || !enabled) return;
    try { e.preventDefault?.(); } catch {}
    holdingRef.current = true;
    voice.start?.();
  }, [wired, enabled, voice]);
  const onPttUp = useCallback((e) => {
    if (!wired) return;
    try { e.preventDefault?.(); } catch {}
    if (!holdingRef.current) return;
    holdingRef.current = false;
    voice.stop?.();
  }, [wired, voice]);
  const onPttCancel = useCallback(() => {
    if (!wired) return;
    if (!holdingRef.current) return;
    holdingRef.current = false;
    voice.cancel?.();
  }, [wired, voice]);

  const _tone =
    autoActive && autoVoice.phase === 'awaiting' ? 'bg-blue-500/80 text-white border-blue-400 animate-pulse'
    : autoActive && (autoVoice.phase === 'stt' || autoVoice.phase === 'exec') ? 'bg-amber-500/80 text-white border-amber-400'
    : autoActive && autoVoice.phase === 'limited' ? 'bg-rose-700/80 text-white border-rose-500'
    : status === 'listening' ? 'bg-red-500/90 text-white border-red-400'
    : status === 'processing' ? 'bg-amber-500/80 text-white border-amber-400'
    : (status === 'detected' || status === 'awaiting') ? 'bg-blue-500/80 text-white border-blue-400 animate-pulse'
    : status === 'done' ? 'bg-emerald-600/80 text-white border-emerald-500'
    : status === 'failed' ? 'bg-rose-700/80 text-white border-rose-500'
    : enabled ? 'bg-[#FF3B30]/85 text-white border-[#FF3B30]'
    : 'bg-black/55 text-white border-white/20 hover:bg-black/70';

  return (
    <div
      className={`pointer-events-auto select-none ${className}`}
      style={style || undefined}
      data-testid={`${testIdPrefix}-root`}
      data-ramo-variant={variant}
      data-ramo-enabled={enabled ? '1' : '0'}
      data-ramo-status={status}
    >
      {isMiniIcon ? (
        <button
          type="button"
          onClick={() => setExpanded((v) => !v)}
          className={`w-9 h-9 inline-flex items-center justify-center rounded-full backdrop-blur-md shadow-lg border transition ${_tone} ${expanded ? 'ring-2 ring-white/40' : ''}`}
          aria-label={expanded ? '라모 AI 패널 접기' : '라모 AI 패널 펼치기'}
          aria-expanded={expanded}
          data-testid={`${testIdPrefix}-capsule`}
          data-ramo-mini="1"
        >
          <Sparkles size={16} />
        </button>
      ) : (
        <button
          type="button"
          onClick={() => setExpanded((v) => !v)}
          className={`inline-flex items-center gap-1.5 pl-2 pr-2.5 py-1.5 rounded-full backdrop-blur-md shadow-lg border transition whitespace-nowrap ${_tone} ${expanded ? 'ring-2 ring-white/40' : ''}`}
          aria-label={expanded ? '라모 AI 패널 접기' : '라모 AI 패널 펼치기'}
          aria-expanded={expanded}
          data-testid={`${testIdPrefix}-capsule`}
          data-ramo-mini="0"
        >
          <span className="text-[11px] leading-none" aria-hidden="true">{DIAMOND}</span>
          <Sparkles size={11} className="opacity-80" />
          <span className="text-[10px] font-black leading-none">{capsuleLabel}</span>
        </button>
      )}

      {expanded && (
        <div
          className="mt-2 w-[14rem] rounded-xl bg-black/75 backdrop-blur-md shadow-xl border border-white/15 overflow-hidden text-white"
          data-testid={`${testIdPrefix}-panel`}
          role="dialog"
          aria-label="라모 AI 도우미"
        >
          <div className="flex items-center justify-between px-2.5 py-1.5 border-b border-white/10">
            <span className="text-[11px] font-black inline-flex items-center gap-1">
              <span aria-hidden="true">{DIAMOND}</span>
              라모 AI 도우미
            </span>
            <button
              type="button"
              onClick={() => setExpanded(false)}
              className="w-5 h-5 inline-flex items-center justify-center text-white/80 hover:text-white"
              aria-label="접기"
              data-testid={`${testIdPrefix}-panel-close`}
            >
              <X size={11} />
            </button>
          </div>

          <div className="px-2.5 py-2 space-y-2">
            {/* 상태 */}
            <div className="flex items-center justify-between gap-2"
                 data-testid={`${testIdPrefix}-panel-status`}>
              <span className="text-[10px] text-white/60 font-bold uppercase tracking-wider">상태</span>
              <span className="text-[11px] font-black text-white" data-testid={`${testIdPrefix}-panel-status-label`}>
                {status === 'idle' && wakeReady ? '"라모야" 호출 대기' : (STATUS_LABELS[status] || STATUS_LABELS.idle)}
              </span>
            </div>
            {!!lastResult && (
              <div className="text-[10px] text-white/80 leading-snug"
                   data-testid={`${testIdPrefix}-panel-last-result`}>
                {lastResult}
              </div>
            )}

            {/* 음성 명령 ON/OFF · PTT */}
            <div className="rounded-lg bg-white/5 border border-white/10 px-2 py-1.5"
                 data-testid={`${testIdPrefix}-panel-voice`}>
              <div className="flex items-center justify-between gap-2 mb-1">
                <span className="text-[10px] font-black text-white inline-flex items-center gap-1">
                  <Mic size={10} /> 음성 명령
                </span>
                {wired ? (
                  <button type="button" onClick={() => voice.toggle?.()}
                          className={`text-[9px] font-black px-1.5 py-0.5 rounded-full border ${
                            enabled ? 'bg-emerald-500 text-white border-emerald-400'
                                    : 'bg-white/10 text-white/80 border-white/15'
                          }`}
                          data-testid={`${testIdPrefix}-panel-voice-toggle`}>
                    {enabled ? 'ON' : 'OFF'}
                  </button>
                ) : (
                  <span className="text-[9px] font-black px-1.5 py-0.5 rounded-full bg-white/10 text-white/70">준비 중</span>
                )}
              </div>
              {wired && enabled ? (
                <button type="button"
                        onPointerDown={onPttDown}
                        onPointerUp={onPttUp}
                        onPointerLeave={onPttCancel}
                        onPointerCancel={onPttCancel}
                        onContextMenu={(e) => e.preventDefault()}
                        className={`w-full min-h-[36px] rounded-md border text-[10px] font-black inline-flex items-center justify-center gap-1 transition ${
                          status === 'listening'
                            ? 'bg-red-500 text-white border-red-400 animate-pulse'
                            : 'bg-white/10 text-white border-white/15 hover:bg-white/20'
                        }`}
                        data-testid={`${testIdPrefix}-panel-ptt`}
                        aria-label="길게 눌러서 말하기">
                  <Mic size={11} />
                  {status === 'listening' ? '듣는 중 · 놓으면 전송' : '길게 눌러서 말하기'}
                </button>
              ) : (
                <p className="text-[10px] text-white/55 leading-snug">
                  {wired
                    ? 'ON 버튼을 눌러 음성 명령을 활성화하세요. ON 상태에서 아래 버튼을 길게 눌러 말하면 돼요.'
                    : '향후 "라모야"로 호출하여 상품·공지·타이머를 음성으로 제어할 수 있어요.'}
                </p>
              )}
              <p className="text-[9px] text-white/40 mt-1">
                지원 명령: N번 상품 · 다음·이전 · 오버레이·가격 · 공지 · 타이머.
              </p>
            </div>

            {/* 최근 명령 */}
            <div className="rounded-lg bg-white/5 border border-white/10 px-2 py-1.5"
                 data-testid={`${testIdPrefix}-panel-history`}>
              <div className="flex items-center justify-between gap-2 mb-1">
                <span className="text-[10px] font-black text-white inline-flex items-center gap-1">
                  <History size={10} /> 최근 명령
                </span>
              </div>
              {history.length === 0 ? (
                <p className="text-[10px] text-white/55 leading-snug"
                   data-testid={`${testIdPrefix}-panel-history-empty`}>
                  아직 음성 명령 기록이 없습니다.
                </p>
              ) : (
                <ul className="space-y-0.5 max-h-[6rem] overflow-y-auto">
                  {history.map((h, i) => (
                    <li key={i}
                        className={`text-[10px] leading-snug ${h.ok ? 'text-white/85' : 'text-rose-200/90'}`}>
                      <span className="font-black mr-1">{h.ok ? '✓' : '✗'}</span>
                      {h.text || h.intent}
                    </li>
                  ))}
                </ul>
              )}
            </div>

            {autoVoice && (
              <RamoAutoVoicePanel auto={autoVoice} enabled={wired && enabled} testIdPrefix={testIdPrefix} />
            )}

            {/* STEP MOA-LIVE-VOICE-1C · "라모야" 자동 호출어 토글 · Vosk-browser (Apache 2.0).
                iOS 는 현재 ios_hold 로 비활성 · PTT 백업 유지. */}
            {wakeWord ? (
              <div className="rounded-lg bg-white/5 border border-white/10 px-2 py-1.5"
                   data-testid={`${testIdPrefix}-panel-wake`}>
                <div className="flex items-center justify-between gap-2 mb-1">
                  <span className="text-[10px] font-black text-white inline-flex items-center gap-1">
                    <SettingsIcon size={10} /> "라모야" 자동 호출
                  </span>
                  <button type="button"
                          onClick={() => wakeWord.toggle?.()}
                          disabled={!wired || !enabled || wakeWord.isIOS}
                          className={`text-[9px] font-black px-1.5 py-0.5 rounded-full border ${
                            wakeWord.on
                              ? 'bg-emerald-500 text-white border-emerald-400'
                              : 'bg-white/10 text-white/80 border-white/15'
                          } ${(!wired || !enabled || wakeWord.isIOS) ? 'opacity-50 cursor-not-allowed' : ''}`}
                          data-testid={`${testIdPrefix}-panel-wake-toggle`}>
                    {wakeWord.on ? 'ON' : 'OFF'}
                  </button>
                </div>
                <p className="text-[9px] text-white/55 leading-snug" data-testid={`${testIdPrefix}-panel-wake-state`}>
                  {wakeWord.isIOS
                    ? 'iOS 는 호환성 검증 전까지 PTT 를 사용해 주세요.'
                    : wakeWord.state === 'loading_sdk'
                      ? '호출어 엔진 로드 중…'
                    : wakeWord.state === 'downloading'
                      ? `한국어 모델 다운로드 중… ${Math.max(0, Math.min(100, wakeWord.progress || 0))}% (약 83MB · 최초 1회)`
                    : wakeWord.state === 'initializing_model'
                      ? '모델 초기화 중 (압축 해제·메모리 로드)…'
                    : wakeWord.state === 'mic'
                      ? '마이크 권한 요청 중…'
                    : wakeWord.state === 'recognizer'
                      ? '음성 인식기 준비 중…'
                    : wakeWord.state === 'ready'
                      ? '대기 중 · "라모야" 부르면 자동으로 명령을 들어요.'
                    : wakeWord.state === 'error'
                      ? (wakeWord.error || '호출어 초기화 실패')
                    : wakeWord.state === 'ios_hold'
                      ? (wakeWord.error || 'iOS 호환 검증 전')
                    : !enabled
                      ? '먼저 음성 명령 ON 으로 전환하세요.'
                      : '토글 ON 시 Vosk 로컬 모델이 "라모야" 를 감지해요 (서버 전송 0).'}
                </p>
                {wakeWord.state === 'downloading' && (
                  <div className="mt-1 h-1 rounded-full bg-white/10 overflow-hidden"
                       data-testid={`${testIdPrefix}-panel-wake-progress`}>
                    <div className="h-full bg-emerald-400 transition-all"
                         style={{ width: `${Math.max(0, Math.min(100, wakeWord.progress || 0))}%` }} />
                  </div>
                )}
                {wakeWord.diag && (
                  <details className="mt-1" data-testid={`${testIdPrefix}-panel-wake-diag`}>
                    <summary className="text-[9px] text-white/60 cursor-pointer">진단 로그</summary>
                    <pre className="text-[9px] leading-snug text-white/80 whitespace-pre-wrap break-all mt-1"
                         data-testid={`${testIdPrefix}-panel-wake-diag-body`}>
{`오디오: ${wakeWord.diag.ctxState} · ${wakeWord.diag.sampleRate}Hz · 트랙 ${wakeWord.diag.track}
프레임 ${wakeWord.diag.frames} · 입력레벨 ${(wakeWord.diag.peakRms || 0).toFixed(3)}
partial ${wakeWord.diag.partials} · final ${wakeWord.diag.results}
최근 전사: ${wakeWord.diag.lastText || '-'}
매칭: ${wakeWord.diag.lastMatch ? `${wakeWord.diag.lastMatch.matched ? 'O' : 'X'} ${wakeWord.diag.lastMatch.alias || ''} ← "${wakeWord.diag.lastMatch.text}"` : '-'}
감지 ${wakeWord.diag.detects} · 스킵 ${wakeWord.diag.lastSkip || '-'}
전달: ${wakeWord.diag.handoff || '-'}${wakeWord.diag.capturing ? ' · 명령 녹음 중' : ''}`}
                    </pre>
                  </details>
                )}
                {wakeWord.state === 'error' && typeof wakeWord.retry === 'function' && (
                  <button type="button"
                          onClick={() => wakeWord.retry?.()}
                          className="mt-1 w-full text-[9px] font-black px-2 py-1 rounded-md bg-white/10 border border-white/20 text-white hover:bg-white/20"
                          data-testid={`${testIdPrefix}-panel-wake-retry`}>
                    다시 시도
                  </button>
                )}
              </div>
            ) : (
              <button type="button" disabled
                      className="w-full inline-flex items-center justify-between px-2 py-1.5 rounded-lg bg-white/5 border border-white/10 text-white/60 cursor-not-allowed"
                      data-testid={`${testIdPrefix}-panel-settings`}>
                <span className="text-[10px] font-black inline-flex items-center gap-1">
                  <SettingsIcon size={10} /> 호출어 설정
                </span>
                <span className="text-[9px] text-white/50">준비 중</span>
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default RamoAiCapsule;
